

import React, { useEffect, useState, useRef, useCallback } from "react";
import {
    Box,
    Grid,
    Typography,
    Paper,
    IconButton,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { useForm, useWatch } from "react-hook-form";

import WSSelectField from "../../../Components/Select/WSSelect";
import WSTextField, { TextType, CurrencyType } from "../../../Components/TextBox/WSTextBox";
import { callAPI, Method, fetchAllPaginated } from "../../../commonFunctions/CommonFunctions";
import WSTextAreaField from "../../../Components/WSTextArea/WSTextArea";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmationPopup from "../../../Components/Confirmation/Confirmation";
import AddIcon from "@mui/icons-material/Add";


interface Item {
    ItemId: string;
    ItemName: string;
    ItemCode: string;
    PurchasePrice?: number;
    SellingPrice?: number;
    IsActive: boolean;
    BrandId?: string;
}

interface Brand {
    BrandId: string;
    BrandName: string;
}

interface Warehouse {
    WarehouseId: string;
    WarehouseName: string;
    Location: string;
    IsActive: boolean;
}

interface StockinData {
    ItemId: string;
    WarehouseId: string;
    Quantity: number;
    Remarks: string;
    SerialNumbers: string[];
    PurchasePrice: number;
    SellingPrice: number;
}

const Stockin = () => {
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedId, setSelectedId] = useState<string | number>(0);
    const [isPopupOpen, setPopupOpen] = useState(false);
    const [isScanning, setIsScanning] = useState(false);
    const { showMessage } = useSnackbar();
    const params = useParams();
    const id = params.id;
    const navigate = useNavigate();

    const [items, setItems] = useState<Item[]>([]);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [supplieres, setSuppliers] = useState<any[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);

    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            ItemId: "",
            WarehouseId: "",
            Quantity: "",
            Remarks: "",
            SerialNumbers: "",
            BrandId: "",
            PurchasePrice: "",
            SellingPrice: "",
        },
    });

    // Watch quantity and serial numbers for validation
    const watchedQuantity = useWatch({ control, name: "Quantity" });
    const watchedSerialNumbers = useWatch({ control, name: "SerialNumbers" });

    // Brand is a filter on the Item dropdown only (BrandId is a foreign key on
    // Item, not on the stock-in record) — it is never sent to the backend.
    const watchedBrandId = useWatch({ control, name: "BrandId" });
    const filteredItems = watchedBrandId
        ? items.filter((item) => item.BrandId === watchedBrandId)
        : items;

    const fetchAllData = (url: string) => fetchAllPaginated(url);

    // Fetch dropdown data
    const fetchDropdownData = async () => {
        try {
            setLoading(true);

            const [allItems, allWarehouses, allSuppliers, allBrands] = await Promise.all([
                fetchAllData("items/list"),
                fetchAllData("warehouses/list"),
                fetchAllData("suppliers/list"),
                fetchAllData("brands/list"),
            ]);

            setItems(allItems);
            setWarehouses(allWarehouses);
            setSuppliers(allSuppliers);
            setBrands(allBrands);

        } catch (error) {
            console.error("Failed to fetch dropdown data:", error);
            showMessage("Failed to load dropdown data", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!id || id === "undefined" || id === "null" || id.trim() === "") {
            setIsEditMode(false);
            reset();
            fetchDropdownData();
            return;
        }

        setIsEditMode(true);
        fetchStockinData(id);
        fetchDropdownData();
    }, [id, reset]);

    const fetchStockinData = async (stockId: string) => {
        try {
            setLoading(true);

            const response = await callAPI(`stock-in/${stockId}`, Method.GET);

            if (response?.success && response.data) {
                const stockData: StockinData = response.data;
                reset({
                    ItemId: stockData.ItemId || "",
                    WarehouseId: stockData.WarehouseId || "",
                    Quantity: stockData.Quantity?.toString() || "",
                    Remarks: stockData.Remarks || "",
                    PurchasePrice: stockData.PurchasePrice?.toString() || "",
                    SellingPrice: stockData.SellingPrice?.toString() || "",
                    SerialNumbers: stockData.SerialNumbers?.join(", ") || "",
                });
            } else {
                console.error("❌ No data in response:", response);
                showMessage(response?.message || `Opening stock ${stockId} not found`, "error");
                setIsEditMode(false);
            }
        } catch (error: any) {
            console.error("❌ Fetch error:", error.response?.data || error.message);
            showMessage("Failed to load opening stock data", "error");
            setIsEditMode(false);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to convert comma-separated string to array
    const parseSerialNumbers = (serialNumbersString: string): string[] => {
        if (!serialNumbersString?.trim()) return [];
        return serialNumbersString
            .split(",")
            .map((num) => num.trim())
            .filter((num) => num !== "");
    };

    // QR Code / Serial Number Scanner Handler
    const handleSerialNumberScan = useCallback((event: React.KeyboardEvent) => {
        const target = event.target as HTMLTextAreaElement;
        const text = target.value;

        // Check if Enter key was pressed without Shift (simulates barcode/QR scan)
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();

            // Get the last line/scanned value
            const lines = text.split('\n');
            const lastLine = lines[lines.length - 1].trim();

            if (lastLine && !text.includes(lastLine + ',')) {
                // Append with comma if not already present
                const updatedText = text ? `${text},${lastLine}`.trim() : lastLine;
                setValue("SerialNumbers", updatedText);

                // Focus back to textarea and clear for next scan
                setTimeout(() => {
                    target.focus();
                    target.setSelectionRange(updatedText.length, updatedText.length);
                }, 10);

                showMessage(`Scanned: ${lastLine}`, "info");
                setIsScanning(true);

                // Auto-stop scanning after 3 seconds
                setTimeout(() => setIsScanning(false), 3000);
            }
        }
    }, [setValue, showMessage]);

    const toggleScanningMode = () => {
        setIsScanning(!isScanning);
        if (!isScanning && textareaRef.current) {
            textareaRef.current.focus();
            showMessage("🔍 Scanning mode ON - Scan QR codes now!", "info");
        } else {
            showMessage("Scanning mode OFF", "info");
        }
    };

    const onSubmit = async (data: any) => {
        try {
            setLoading(true);
            const quantity = parseInt(data.Quantity) || 0;
            const serialNumbersArray = parseSerialNumbers(data.SerialNumbers);

            const requestBody: StockinData = {
                ItemId: data.ItemId || "",
                WarehouseId: data.WarehouseId || "",
                Quantity: quantity,
                Remarks: data.Remarks?.trim() || "",
                SerialNumbers: serialNumbersArray,
                PurchasePrice: parseFloat(data.PurchasePrice) || 0,
                SellingPrice: parseFloat(data.SellingPrice) || 0,
            };

            let response;
            if (isEditMode && id) {
                response = await callAPI(`stock-in/${id}`, Method.PATCH, requestBody);
            } else {
                response = await callAPI("stock-in/", Method.POST, requestBody);
            }

            if (response?.success) {
                showMessage(
                    isEditMode ? "Opening stock updated successfully!" : "Opening stock created successfully!",
                    "success"
                );
                navigate("/stockentry/list");
            } else {
                showMessage(response?.detail || "Operation failed", "error");
            }
        } catch (error: any) {
            console.error("❌ Submit error:", error);
            showMessage(error.response?.data?.detail || "Operation failed", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = () => {
        if (!id) return;
        setSelectedId(id);
        setPopupOpen(true);
    };

    return (
        <Box sx={{ p: 2, background: "#f0f0f0", minHeight: "100vh" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
                        {isEditMode ? "Stock in details" : "Stock in details"}
                    </Typography>
                </Box>
                <Box display="flex" gap={1}>
                    {isEditMode && (
                        <IconButton
                            sx={{ background: "black", color: "#fff" }}
                            onClick={handleDeleteClick}
                            disabled={loading}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                    <Tooltip title={isScanning ? "Stop Scanning" : "Start QR Scanning"}>
                        <IconButton
                            sx={{
                                background: isScanning ? "#4caf50" : "#2196f3",
                                color: "#fff",
                                "&:hover": {
                                    backgroundColor: isScanning ? "#45a049" : "#1976d2"
                                }
                            }}
                            onClick={toggleScanningMode}
                            disabled={loading}
                        >
                            <QrCodeScannerIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Paper
                sx={{
                    borderRadius: "16px",
                    p: 3,
                    background: "#fafafa",
                    position: "relative"
                }}
            >
                {loading && (
                    <Box sx={{
                        position: "absolute",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(255,255,255,0.9)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        zIndex: 10, borderRadius: "16px"
                    }}>
                        <CircularProgress />
                    </Box>
                )}

                <Box display="flex" justifyContent="space-between" mb={7}>
                    <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
                        {isEditMode ? "Opening Stock Details" : "New Opening Stock"}
                        {isScanning && (
                            <Typography
                                component="span"
                                sx={{
                                    color: "#4caf50",
                                    fontSize: "14px",
                                    ml: 1,
                                    fontWeight: 500
                                }}
                            >
                                🔍 SCANNING...
                            </Typography>
                        )}
                    </Typography>
                    <Tooltip title={isEditMode ? "Update Stock Entry" : "Create Stock Entry"} arrow>
                        <IconButton
                            sx={{ background: "#000", color: "#fff" }}
                            onClick={handleSubmit(onSubmit)}
                            disabled={!isValid || loading}
                        >
                            <SaveIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Grid container rowSpacing={8} columnSpacing={5}>
                    <Grid item xs={4}>
                        <WSSelectField
                            name="BrandId"
                            label="Brand"
                            id="BrandId"
                            control={control}
                            inlineSearch
                            onChange={() => setValue("ItemId", "")}
                            options={brands.map(brand => ({
                                value: brand.BrandId,
                                label: brand.BrandName
                            }))}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <WSSelectField
                            name="ItemId"
                            label="Item Name"
                            id="ItemId"
                            control={control}
                            inlineSearch
                            onChange={(event: any) => {
                                // Pre-fill prices from the Item Master; the user can still override them.
                                const itemId = event?.target?.value ?? event;
                                const picked = items.find((item) => item.ItemId === itemId);
                                if (picked) {
                                    setValue("PurchasePrice", picked.PurchasePrice?.toString() ?? "");
                                    setValue("SellingPrice", picked.SellingPrice?.toString() ?? "");
                                }
                            }}
                            matchAnyWord
                            options={filteredItems.map(item => ({
                                value: item.ItemId,
                                label: item.ItemName
                            }))}
                        />
                    </Grid>
                    <Grid item xs="auto" display="flex" alignItems="flex-start" sx={{ pl: '12px !important' }}>
                        <Box
                            onClick={() =>
                                navigate("/stockin/itemmaster", { state: { from: "stockin" } })
                            }
                            sx={{
                                height: "36px",
                                width: "36px",
                                borderRadius: "10px",
                                background: "#f3f3f3",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                flexShrink: 0,
                                border: "1px solid #d1d5db",
                                "&:hover": {
                                    background: "#e0e0e0",
                                },
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </Box>
                    </Grid>

                    <Grid item xs={4}>
                        <WSSelectField
                            name="WarehouseId"
                            label="Warehouse"
                            id="WarehouseId"
                            control={control}
                            inlineSearch
                            options={warehouses.map(warehouse => ({
                                value: warehouse.WarehouseId,
                                label: warehouse.WarehouseName
                            }))}
                        />
                    </Grid>

                    <Grid item xs="auto" display="flex" alignItems="flex-start" sx={{ pl: '12px !important' }}>
                        <Box
                            onClick={() =>
                                navigate("/stockin/warehouse", { state: { from: "stockin" } })
                            }
                            sx={{
                                height: "36px",
                                width: "36px",
                                borderRadius: "10px",
                                background: "#f3f3f3",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                flexShrink: 0,
                                border: "1px solid #d1d5db",
                                "&:hover": {
                                    background: "#e0e0e0",
                                },
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </Box>
                    </Grid>

                    <Grid item xs={4}>
                        <WSSelectField
                            name="supplierId"
                            label="Supplier"
                            id="supplier"
                            control={control}
                            inlineSearch
                            options={supplieres.map(supplier => ({
                                value: supplier.SupplierId,
                                label: supplier.SupplierName
                            }))}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <WSTextField
                            name="Quantity"
                            label="Quantity"
                            id="Quantity"
                            control={control}
                            type={TextType.AllowOnlyNumber}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <WSTextField
                            name="PurchasePrice"
                            label="Purchase Price"
                            id="PurchasePrice"
                            control={control}
                            type={TextType.Currency}
                            currency={CurrencyType.Dirham}
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <WSTextField
                            name="SellingPrice"
                            label="Selling Price"
                            id="SellingPrice"
                            control={control}
                            type={TextType.Currency}
                            currency={CurrencyType.Dirham}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <WSTextAreaField
                            name="SerialNumbers"
                            label="Serial Numbers"
                            id="SerialNumbers"
                            control={control}
                            inputRef={textareaRef}
                            onKeyDown={handleSerialNumberScan}
                            // helperText={
                            //     watchedQuantity 
                            //         ? `Enter/scan exactly ${watchedQuantity} serial numbers (comma separated). Scanned: ${parseSerialNumbers(watchedSerialNumbers || "").length}/${watchedQuantity}`
                            //         : "Enter/scan serial numbers (comma separated) or press Enter after each QR scan"
                            // }
                            rows={4}
                            disabled={loading}
                        />
                    </Grid>

                    <Grid item xs={6} >
                        <WSTextAreaField
                            name="Remarks"
                            label="Remarks"
                            id="Remarks"
                            control={control}
                            rows={4}
                        />
                    </Grid>

                </Grid>
            </Paper>
            <ConfirmationPopup
                type="DELETE"
                apiUrl="stock-in"
                id={selectedId}
                confirmMessage="Are you sure you want to delete this stockin?"
                open={isPopupOpen}
                onClose={() => setPopupOpen(false)}
                onConfirm={async (data) => {
                    if (!selectedId) return;

                    if (data?.success === true) {
                        showMessage("stockin deleted successfully!", "success");
                        navigate("/stockentry/list");
                    } else {
                        showMessage(data?.message || "Delete failed", "error");
                    }

                    setPopupOpen(false);
                }}
            />
        </Box>
    );
};

export default Stockin;